import { useEffect, useState, FormEvent, useRef } from 'react';
import { api, type Me, type ReportDashboard, type CdrRow, type ReportRun, type ScheduledReport, type WxcCallAccountingResponse, type LgwCallVolumeResponse, type SystemCdrReportResponse, type WxccSampleReportResponse, type WxccLiveDashboardResponse, type WxccTasksHistoricalResponse, type WxcCdrRowsResponse, type WxccReportTemplate, type WxccTemplateRunResponse, type WxccReportExport } from '../lib/api';

type Tab = 'dashboard' | 'wxc' | 'wxcc' | 'cucm' | 'uccx' | 'lgw' | 'templates' | 'runs' | 'schedules';
const TAB_LABEL: Record<Tab, string> = {
  dashboard: 'Dashboard',
  wxc:  'WxC Report',
  wxcc: 'WxCC Reports',
  cucm: 'CUCM Reports',
  uccx: 'UCCX Reports',
  lgw:  'LGW Reports',
  templates: 'Reports Template',
  runs: 'Runs',
  schedules: 'Schedules',
};

export default function Reports({ me }: { me: Me }) {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [tenantId, setTenantId] = useState<string>(me.tenant_id === 'GLOBAL' ? 'GLOBAL' : me.tenant_id);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-gray-500 text-sm mt-1">
            Tenant voice operations — call volume, CDR, on-demand exports, and recurring email digests.
          </p>
        </div>
        {me.role === 'super_admin' && (
          <div className="flex gap-2 items-center text-sm">
            <span className="text-gray-500">Tenant:</span>
            <input value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                   className="px-3 py-1.5 text-sm rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent w-40 font-mono" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-tnh-border dark:border-tnh-border-dark overflow-x-auto">
        {(['dashboard','wxc','wxcc','cucm','uccx','lgw','templates','runs','schedules'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap ${
                    tab === t ? 'border-tnh-accent text-tnh-accent font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}>
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab me={me} tenantId={tenantId} />}
      {tab === 'wxc'       && <WxcReportTab />}
      {tab === 'wxcc'      && <WxccReportTab />}
      {tab === 'cucm'      && <SystemReportTab system="cucm" tenantId={tenantId} />}
      {tab === 'uccx'      && <SystemReportTab system="uccx" tenantId={tenantId} />}
      {tab === 'lgw'       && <LgwReportTab />}
      {tab === 'templates' && <ReportsTemplateTab tenantId={tenantId} />}
      {tab === 'runs'      && <RunsTab me={me} tenantId={tenantId} />}
      {tab === 'schedules' && <SchedulesTab me={me} tenantId={tenantId} />}
    </div>
  );
}

// ===========================================================================
// Reusable date-range picker — same look across all 5 new sub-menus.
// ===========================================================================
function isoLocal(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function DateRange({ from, to, onFrom, onTo, onRun, busy }: {
  from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void; onRun: () => void; busy: boolean;
}) {
  return (
    <div className="card p-3 flex items-end gap-3 flex-wrap">
      <label className="block">
        <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">From</span>
        <input type="datetime-local" value={from} onChange={(e) => onFrom(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">To</span>
        <input type="datetime-local" value={to} onChange={(e) => onTo(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
      </label>
      <button onClick={onRun} disabled={busy}
        className="px-4 py-2 rounded-lg bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
        {busy ? 'Running…' : 'Run report'}
      </button>
    </div>
  );
}

// ============================================================================
// BUILD-TASK-format-durations-and-fix-mappings Fix 1 + Fix 7 — cell formatters.
//
// Splits the UI display path from the CSV export path:
//   formatCellForDisplay(value, type) — used in <td>, returns 'HH:MM:SS' /
//                                       'XX.X%' / 'YYYY-MM-DD HH:MM:SS'.
//   formatCellForCsv(value, type)     — used by downloadCsv, returns the
//                                       RAW number (ms / 0-1 ratio / ms-epoch)
//                                       so the file round-trips into Excel
//                                       as an arithmetic value.
//
// Server emits raw values + a column_types map keyed by column id. The
// SPA renderer reads column_types[c.id] when applying the formatter.
// ============================================================================
type CellType = 'duration_ms' | 'ratio' | 'timestamp_ms' | 'int' | 'string';

export function formatDurationForDisplay(ms: number | string | null | undefined): string {
  if (ms == null || ms === '—' || ms === '') return '—';
  const n = typeof ms === 'number' ? ms : Number(ms);
  if (!Number.isFinite(n)) return '—';
  const totalSec = Math.round(n / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (k: number) => String(k).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function formatRatioForDisplay(v: number | string | null | undefined): string {
  if (v == null || v === '—' || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  // Cisco's DIVIDE formula yields a 0-1 float (e.g. 0.875 = 87.5%); if a
  // server happens to ship it as a percentage already (>1.0), keep it.
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
}

export function formatTimestampForDisplay(ms: number | string | null | undefined): string {
  if (ms == null || ms === '—' || ms === '') return '—';
  const n = typeof ms === 'number' ? ms : Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (k: number) => String(k).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatCellForDisplay(value: any, type: CellType | undefined): string {
  if (value === undefined || value === null || value === '—') return '—';
  if (!type) return String(value);
  if (type === 'duration_ms')  return formatDurationForDisplay(value);
  if (type === 'ratio')        return formatRatioForDisplay(value);
  if (type === 'timestamp_ms') return formatTimestampForDisplay(value);
  return String(value);
}

export function formatCellForCsv(value: any, _type: CellType | undefined): any {
  // Raw round-trippable. Excel ingests ms-since-epoch as a number; the user
  // can apply a TIME() or =A1/1000/86400 transform on import. Same for
  // durations (ms) and ratios (0-1 float). The em-dash sentinel becomes
  // an empty cell so SUM()/AVG() don't trip on it.
  if (value === '—' || value === undefined || value === null) return '';
  return value;
}

function downloadCsv(filename: string, rows: Array<Record<string, any>>) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]!);
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ProbeTrailBlock({ probes, message }: { probes?: Array<{ path: string; status: number; body_excerpt?: string }>; message?: string }) {
  return (
    <div className="card p-4 border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 space-y-2">
      <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">Data source not reachable</div>
      {message && <div className="text-xs text-gray-700 dark:text-gray-200">{message}</div>}
      {probes && probes.length > 0 && (
        <details className="text-[11px] font-mono">
          <summary className="cursor-pointer text-amber-700 dark:text-amber-300">Show probe trail ({probes.length})</summary>
          <ol className="mt-1.5 space-y-0.5 max-h-48 overflow-auto">
            {probes.map((p, i) => (
              <li key={i} className="text-gray-600 dark:text-gray-400">
                [{String(p.status).padStart(3)}] {p.path}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

// ===========================================================================
// #1 — WxC Report (ISI-style)
// ===========================================================================
function WxcReportTab() {
  const [from, setFrom] = useState(isoLocal(Date.now() - 7 * 86400 * 1000));
  const [to, setTo]     = useState(isoLocal(Date.now()));
  // v4 — full ISI filter set
  const [calling, setCalling]     = useState('');
  const [called, setCalled]       = useState('');
  const [direction, setDirection] = useState('');
  const [outcome, setOutcome]     = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [location, setLocation]   = useState('');
  const [callType, setCallType]   = useState('');
  const [minDur, setMinDur]       = useState('');
  const [maxDur, setMaxDur]       = useState('');
  // Time-window classifier knobs (port of webex-cdr-cloud/utils/time_windows.py)
  const [bizStart, setBizStart]     = useState('06:00');
  const [bizEnd, setBizEnd]         = useState('18:00');
  const [eveningEnd, setEveningEnd] = useState('23:00');
  const [tzOffset, setTzOffset]     = useState<number>(-new Date().getTimezoneOffset());
  const [data, setData] = useState<WxcCallAccountingResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.reportsWxcCallAccounting({
        from_date: new Date(from).toISOString(),
        to_date:   new Date(to).toISOString(),
        calling: calling || undefined,
        called: called || undefined,
        direction: direction || undefined,
        outcome: outcome || undefined,
        user_email: userEmail || undefined,
        location: location || undefined,
        call_type: callType || undefined,
        min_duration: minDur ? Number(minDur) : undefined,
        max_duration: maxDur ? Number(maxDur) : undefined,
        tz_offset_minutes: tzOffset,
        biz_start: bizStart, biz_end: bizEnd, evening_end: eveningEnd,
      });
      setData(r);
    } catch (e: any) { setErr(e?.message ?? 'fetch failed'); } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500">
        ISI Telemanagement-style call accounting from Webex Calling Detailed Call History. Cost figures are intentionally
        omitted — Webex CDR carries no rate plan.
      </div>
      <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={run} busy={busy} />
      {/* v4 — extended filter strip (port of webex-cdr-cloud filter set) */}
      <div className="card p-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <input value={calling}   onChange={(e) => setCalling(e.target.value)}   placeholder="Calling number"   className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono" />
        <input value={called}    onChange={(e) => setCalled(e.target.value)}    placeholder="Called number"    className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono" />
        <select value={direction} onChange={(e) => setDirection(e.target.value)} className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs">
          <option value="">Any direction</option><option value="INBOUND">Inbound</option><option value="OUTBOUND">Outbound</option><option value="INTERNAL">Internal</option>
        </select>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs">
          <option value="">Any outcome</option><option value="answered">Answered</option><option value="noanswer">No answer</option><option value="busy">Busy</option><option value="abandoned">Abandoned</option><option value="failed">Failed</option>
        </select>
        <input value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder="User (email substring)" className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs" />
        <input value={location}  onChange={(e) => setLocation(e.target.value)}  placeholder="Location substring"     className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs" />
        <input value={callType}  onChange={(e) => setCallType(e.target.value)}  placeholder="Call type substring"    className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs" />
        <input value={minDur}    onChange={(e) => setMinDur(e.target.value)}    placeholder="Min duration (s)" type="number" min="0" className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs" />
        <input value={maxDur}    onChange={(e) => setMaxDur(e.target.value)}    placeholder="Max duration (s)" type="number" min="0" className="px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs" />
      </div>
      <div className="card p-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-xs">
        <label className="flex items-center gap-2"><span className="text-gray-500">Business start</span><input value={bizStart} onChange={(e) => setBizStart(e.target.value)} placeholder="06:00" className="flex-1 px-2 py-1 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent" /></label>
        <label className="flex items-center gap-2"><span className="text-gray-500">Business end</span><input value={bizEnd} onChange={(e) => setBizEnd(e.target.value)} placeholder="18:00" className="flex-1 px-2 py-1 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent" /></label>
        <label className="flex items-center gap-2"><span className="text-gray-500">Evening end</span><input value={eveningEnd} onChange={(e) => setEveningEnd(e.target.value)} placeholder="23:00" className="flex-1 px-2 py-1 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent" /></label>
        <label className="flex items-center gap-2"><span className="text-gray-500">TZ offset (min)</span><input type="number" value={tzOffset} onChange={(e) => setTzOffset(Number(e.target.value))} className="flex-1 px-2 py-1 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent" /></label>
      </div>
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {data && !data.reachable && <ProbeTrailBlock probes={data.probes} message={data.message} />}
      {data && data.reachable && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {[
              { l: 'Total calls',   v: data.totals?.calls ?? 0 },
              { l: 'Answered',      v: `${data.totals?.answered ?? 0} (${data.totals?.answer_rate_pct ?? 0}%)` },
              { l: 'Inbound',       v: data.totals?.inbound ?? 0 },
              { l: 'Outbound',      v: data.totals?.outbound ?? 0 },
              { l: 'Avg talk',      v: `${data.totals?.avg_talk_sec ?? 0}s` },
              { l: 'Total duration',v: `${Math.round((data.totals?.total_duration_sec ?? 0) / 60)}m` },
            ].map((k) => (
              <div key={k.l} className="card p-3">
                <div className="text-xl font-semibold">{String(k.v)}</div>
                <div className="text-[11px] text-gray-500">{k.l}</div>
              </div>
            ))}
          </div>
          {(data.monthly_windows ?? []).length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
                <div className="text-sm font-semibold">Monthly × Time-Window (port of webex-cdr-cloud)</div>
                <span className="text-[11px] text-gray-500">Business {data.time_windows?.business} · Evening {data.time_windows?.evening} · Overnight {data.time_windows?.overnight} · TZ offset {data.tz_offset_minutes ?? 0}m</span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/40"><tr>{['Month', 'Business', 'Evening', 'Overnight', 'After-hours', 'Grand total'].map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold">{c}</th>)}</tr></thead>
                <tbody>
                  {(data.monthly_windows ?? []).map((m) => (
                    <tr key={m.year_month} className="border-t border-tnh-border dark:border-tnh-border-dark">
                      <td className="px-3 py-1.5 font-mono">{m.year_month}</td>
                      <td className="px-3 py-1.5">{m.business}</td>
                      <td className="px-3 py-1.5">{m.evening}</td>
                      <td className="px-3 py-1.5">{m.overnight}</td>
                      <td className="px-3 py-1.5">{m.after_hours}</td>
                      <td className="px-3 py-1.5 font-semibold">{m.grand_total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-3">
            <CountTable title="By day" rows={data.by_day ?? []} cols={['day', 'calls']} />
            <CountTable title="Trunk utilization" rows={data.trunk_utilization ?? []} cols={['trunk', 'calls']} />
            <CountTable title="Top extensions" rows={(data.top_extensions ?? []).map((e) => ({ extension: e.extension, calls: e.calls, duration_sec: e.total_sec }))} cols={['extension', 'calls', 'duration_sec']} />
            <CountTable title="Top destinations" rows={(data.top_numbers ?? []).map((e) => ({ number: e.number, calls: e.calls, duration_sec: e.total_sec }))} cols={['number', 'calls', 'duration_sec']} />
          </div>
          <div className="text-[11px] text-gray-500">
            {data.cost_note} · {data.windows_fetched ?? 0} CDR window(s), {data.pages_fetched ?? 0} page(s) fetched · {data.record_count ?? 0} of {data.raw_total ?? 0} rows after filters.
          </div>
        </>
      )}
    </div>
  );
}

function CountTable({ title, rows, cols }: { title: string; rows: Array<Record<string, any>>; cols: string[] }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        {rows.length > 0 && (
          <button onClick={() => downloadCsv(`${title.toLowerCase().replace(/\s+/g, '_')}.csv`, rows)}
            className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-900/40">
          <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-gray-500">no data</td></tr>}
          {rows.slice(0, 30).map((r, i) => (
            <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
              {cols.map((c) => <td key={c} className="px-3 py-1.5">{String(r[c] ?? '—')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// #2 — WxCC Reports (sample-driven)
// ===========================================================================
function WxccReportTab() {
  return (
    <div className="space-y-6">
      <WxccLiveDashboardPanel />
      <WxccPerTeamReportPanel />
      <WxccQuickReportsPanel />
      <WxccDevicesPanel />
      <WxccSampleReportPanel />
    </div>
  );
}

// ── Per-team Report runner — 15-column Analyzer format (visualization 66891) ─
// Picks a WxCC team → resolves its agents → fetches CDR rows for the chosen
// date+time window → renders the exact 15 columns from the user-supplied
// template + bottom summary. CSV export. When the CDR scope is gated, shows
// the honest probe-fallback panel.
const ANALYZER_15_COLS = [
  'Start time (Pacific)',
  'Start time (UTC)',
  'Calling number',
  'Called number',
  'User',
  'Direction',
  'Call type',
  'Call outcome',
  'Answered',
  'Duration (s)',
  'Ring duration (s)',
  'Location',
  'Calling line ID',
  'Called line ID',
  'Correlation ID',
] as const;

function toPacific(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour12: false });
  } catch { return iso; }
}
function toUtcStr(iso: string): string {
  if (!iso) return '';
  try { return new Date(iso).toISOString().replace('T', ' ').replace('Z', ' UTC'); }
  catch { return iso; }
}
function callOutcomeOf(r: any): string {
  if (r.answerTime || r.answered === true || Number(r.duration || 0) > 0) return 'Answered';
  const rr = String(r.releaseReason ?? r.terminationReason ?? '').toLowerCase();
  if (rr.includes('busy')) return 'Busy';
  if (rr.includes('abandon')) return 'Abandoned';
  if (rr.includes('fail') || rr.includes('error') || rr.includes('congestion')) return 'Failed';
  return 'NoAnswer';
}
function mapRowTo15Cols(r: any): Record<string, string> {
  const start = String(r.startTime ?? r.originatingTime ?? r.startedDateTime ?? '');
  return {
    'Start time (Pacific)': toPacific(start),
    'Start time (UTC)':     toUtcStr(start),
    'Calling number':       String(r.callingNumber ?? ''),
    'Called number':        String(r.calledNumber ?? ''),
    'User':                 String(r.userEmail ?? r.userName ?? ''),
    'Direction':            String(r.direction ?? ''),
    'Call type':            String(r.callType ?? ''),
    'Call outcome':         callOutcomeOf(r),
    'Answered':             (r.answerTime || r.answered === true) ? 'Yes' : 'No',
    'Duration (s)':         String(r.duration ?? r.callDuration ?? r.connectedDuration ?? 0),
    'Ring duration (s)':    String(r.ringDuration ?? r.ringTime ?? 0),
    'Location':             String(r.locationName ?? r.location ?? r.userLocation ?? ''),
    'Calling line ID':      String(r.callingLineId ?? r.callingName ?? ''),
    'Called line ID':       String(r.calledLineId ?? r.calledName ?? ''),
    'Correlation ID':       String(r.correlationId ?? r.localCallId ?? r.callId ?? ''),
  };
}

function WxccPerTeamReportPanel() {
  const [teams, setTeams] = useState<Array<{ id: string; name: string }> | null>(null);
  const [agents, setAgents] = useState<Array<{ id: string; teamIds?: string[]; email?: string; loginName?: string; userName?: string; displayName?: string }> | null>(null);
  const [teamSel, setTeamSel] = useState<string>('');     // single team id, '' = pick prompt, '*' = all teams
  const [from, setFrom] = useState(isoLocal(Date.now() - 7 * 86400 * 1000));
  const [to, setTo]     = useState(isoLocal(Date.now()));
  const [result, setResult] = useState<WxcCdrRowsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.wxccTeams().catch(() => ({ teams: [] as any[] })),
      api.wxccAgents().catch(() => ({ agents: [] as any[] })),
    ]).then(([t, a]) => {
      setTeams((t.teams ?? []).map((x: any) => ({ id: x.id, name: x.name ?? x.id })));
      setAgents(a.agents ?? []);
    });
  }, []);

  const emailsForTeam = (teamId: string): string[] => {
    if (!agents) return [];
    if (teamId === '*') {
      return [...new Set(agents.map((u) => String(u.email ?? u.loginName ?? u.userName ?? '').toLowerCase()).filter(Boolean))];
    }
    return [...new Set(
      agents
        .filter((u) => Array.isArray(u.teamIds) && u.teamIds!.includes(teamId))
        .map((u) => String(u.email ?? u.loginName ?? u.userName ?? '').toLowerCase())
        .filter(Boolean),
    )];
  };

  const run = async () => {
    if (!teamSel) { setErr('Pick a team or "All teams".'); return; }
    const emails = emailsForTeam(teamSel);
    if (emails.length === 0) {
      setErr('Selected team has no members with email addresses we can match against CDR rows.');
      return;
    }
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await api.wxcCdrRows({
        from_date: new Date(from).toISOString(),
        to_date:   new Date(to).toISOString(),
        user_emails: emails,
      });
      setResult(r);
    } catch (e: any) { setErr(e?.message ?? 'fetch failed'); } finally { setBusy(false); }
  };

  const teamName = teamSel === '*' ? 'All teams' : (teams ?? []).find((t) => t.id === teamSel)?.name ?? '';
  const sheetName = teamName ? `${teamName} ${from.slice(11, 16)}-${to.slice(11, 16)}` : 'Per-team report';
  const fifteenCol = (result?.rows ?? []).map(mapRowTo15Cols);

  return (
    <div className="card p-5 space-y-3">
      <div>
        <div className="text-base font-semibold">Per-team Report (Analyzer 15-column format)</div>
        <div className="text-xs text-gray-500">
          Cisco WxCC Analyzer visualization <code className="font-mono">66891</code> shape.
          Resolves a team's agents → filters live CDR (<code className="font-mono">cdr_feed</code>) by their emails → renders the exact 15 columns + summary.
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 items-end">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Team</span>
          <select value={teamSel} onChange={(e) => setTeamSel(e.target.value)} disabled={!teams}
                  className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="">— Select team —</option>
            <option value="*">All teams (batch)</option>
            {(teams ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">From</span>
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">To</span>
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)}
                 className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </label>
        <button onClick={run} disabled={busy || !teamSel}
                className="px-4 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? 'Fetching…' : 'Run report'}
        </button>
      </div>
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {result && !result.reachable && <ProbeTrailBlock probes={result.probes} message={result.message} />}
      {result && result.reachable && (
        <div className="card overflow-hidden">
          <div className="px-3 py-2 flex items-center justify-between border-b border-tnh-border dark:border-tnh-border-dark flex-wrap gap-2">
            <div className="text-sm font-semibold">{sheetName} — {result.total} call(s)</div>
            {fifteenCol.length > 0 && (
              <button onClick={() => downloadCsv(`${sheetName.replace(/[^A-Za-z0-9._-]/g, '_')}.csv`, [...fifteenCol, { 'Start time (Pacific)': `Total incoming calls: ${result.total}` } as any])}
                      className="text-xs text-tnh-accent hover:underline">↓ CSV (15-col)</button>
            )}
          </div>
          <div className="overflow-x-auto max-h-[28rem]">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                <tr>{ANALYZER_15_COLS.map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {fifteenCol.length === 0 && <tr><td colSpan={15} className="px-3 py-8 text-center text-gray-500">No matching CDR rows in this window for the team members.</td></tr>}
                {fifteenCol.slice(0, 500).map((r, i) => (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                    {ANALYZER_15_COLS.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap">{r[c] || '—'}</td>)}
                  </tr>
                ))}
                {fifteenCol.length > 0 && (
                  <tr className="border-t-2 border-tnh-accent bg-tnh-accent/5 font-semibold">
                    <td colSpan={15} className="px-3 py-1.5">Total incoming calls: {result.total}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── A. Live Dashboard (port of webex-cc-tool Dashboard tab) ─────────────────
// Two halves: WxCC entities (via wxcc-svc/live-dashboard) + Webex Hub
// (devices/workspaces/people via wxc-svc). Auto-refresh 30s, partial failure
// in either half is shown honestly (the other still renders).
function WxccLiveDashboardPanel() {
  const [cc, setCc] = useState<WxccLiveDashboardResponse | null>(null);
  const [hub, setHub] = useState<{ devices: number | null; online: number | null; unassigned: number | null; workspaces: number | null; people: number | null; errors: Record<string, string | null> } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    const safe = <T,>(p: Promise<T>): Promise<T | null> => p.then((v) => v).catch(() => null);
    const [ccR, devR, wsR, peopleR] = await Promise.all([
      safe(api.wxccLiveDashboard()),
      safe(api.wxcHubDevices(undefined, 500)),
      safe(api.wxcHubWorkspaces(undefined, 500)),
      safe(api.wxcHubPeople(undefined, 1)),
    ]);
    setCc(ccR);
    setHub({
      devices:    devR?.total ?? null,
      online:     devR?.online ?? null,
      unassigned: devR?.unassigned ?? null,
      workspaces: wsR?.total ?? null,
      people:     peopleR?.total ?? null,
      errors: {
        devices:    devR ? null : 'unreachable',
        workspaces: wsR  ? null : 'unreachable',
        people:     peopleR ? null : 'unreachable',
      },
    });
    setBusy(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-base font-semibold">Live Dashboard</div>
          <div className="text-xs text-gray-500">Concurrent KPIs across the Webex Contact Center config APIs and the Webex Hub. Auto-refresh 30s.</div>
        </div>
        <button onClick={load} disabled={busy} className="btn-ghost text-sm disabled:opacity-50">{busy ? 'Refreshing…' : '↻ Refresh now'}</button>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Webex Contact Center</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          <LiveTile label="Entry points" val={cc?.counts.entry_points ?? null} err={cc?.errors.entry_points ?? null} />
          <LiveTile label="Queues"       val={cc?.counts.queues       ?? null} err={cc?.errors.queues       ?? null} />
          <LiveTile label="Teams"        val={cc?.counts.teams        ?? null} err={cc?.errors.teams        ?? null} />
          <LiveTile label="Users"        val={cc?.counts.users        ?? null} err={cc?.errors.users        ?? null} />
          <LiveTile label="Sites"        val={cc?.counts.sites        ?? null} err={cc?.errors.sites        ?? null} />
          <LiveTile label="Dial numbers" val={cc?.counts.dial_numbers ?? null} err={cc?.errors.dial_numbers ?? null} />
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Webex Hub</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          <LiveTile label="Devices"      val={hub?.devices    ?? null} err={hub?.errors.devices ?? null} />
          <LiveTile label="Online"       val={hub?.online     ?? null} err={hub?.errors.devices ?? null} />
          <LiveTile label="Unassigned"   val={hub?.unassigned ?? null} err={hub?.errors.devices ?? null} />
          <LiveTile label="Workspaces"   val={hub?.workspaces ?? null} err={hub?.errors.workspaces ?? null} />
          <LiveTile label="People"       val={hub?.people     ?? null} err={hub?.errors.people ?? null} />
        </div>
      </div>
      {cc?.note && <div className="text-[11px] text-gray-500">{cc.note}</div>}
    </div>
  );
}
function LiveTile({ label, val, err }: { label: string; val: number | null; err: string | null }) {
  return (
    <div className="rounded-md border border-tnh-border dark:border-tnh-border-dark p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-xl font-semibold mt-0.5 ${err ? 'text-rose-600 dark:text-rose-400' : ''}`}>
        {err ? '—' : (val == null ? '…' : val.toLocaleString())}
      </div>
      {err && <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-0.5 truncate" title={err}>{err}</div>}
    </div>
  );
}

// ── B. Quick Reports — historical tasks via /organization/{orgId}/task ──────
function WxccQuickReportsPanel() {
  const [from, setFrom] = useState(isoLocal(Date.now() - 7 * 86400 * 1000));
  const [to, setTo]     = useState(isoLocal(Date.now()));
  const [r, setR]       = useState<WxccTasksHistoricalResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await api.wxccTasksHistorical(new Date(from).getTime(), new Date(to).getTime(), 100);
      setR(res);
    } catch (e: any) { setErr(e?.message ?? 'fetch failed'); } finally { setBusy(false); }
  };
  return (
    <div className="card p-5 space-y-3">
      <div>
        <div className="text-base font-semibold">Quick Reports — historical tasks</div>
        <div className="text-xs text-gray-500">
          Filtered fetch from <code className="font-mono">/organization/&#123;orgId&#125;/task</code> via the from/to epoch-ms parameters.
          NOT the Search API. Per the webex-cc-tool reference behavior.
        </div>
      </div>
      <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={run} busy={busy} />
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {r && (
        r.tasks.length === 0
          ? <div className="text-sm text-gray-500">{r.error || r.note || `No tasks in window ${from} → ${to}.`}</div>
          : (
            <div className="card overflow-hidden">
              <div className="px-3 py-2 flex items-center justify-between border-b border-tnh-border dark:border-tnh-border-dark">
                <div className="text-sm font-semibold">{r.total} task(s)</div>
                <button onClick={() => downloadCsv('wxcc-historical-tasks.csv', r.tasks)} className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                    <tr>{['#', 'ID', 'ANI', 'DNIS', 'Status', 'Start'].map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold whitespace-nowrap">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {r.tasks.slice(0, 100).map((t, i) => (
                      <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                        <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                        <td className="px-3 py-1.5 font-mono">{String(t.id ?? t.taskId ?? '—').slice(0, 20)}</td>
                        <td className="px-3 py-1.5 font-mono">{String(t.ani ?? t.callingNumber ?? '—')}</td>
                        <td className="px-3 py-1.5 font-mono">{String(t.dnis ?? t.calledNumber ?? '—')}</td>
                        <td className="px-3 py-1.5">{String(t.status ?? t.state ?? '—')}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{String(t.startedDateTime ?? t.startTime ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
      )}
    </div>
  );
}

// ── C. Devices — Webex Hub devices listing (spark-admin:devices_read OK) ────
function WxccDevicesPanel() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [stats, setStats] = useState<{ total: number; online: number; offline: number; unassigned: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.wxcHubDevices(undefined, 500);
      setRows(r.devices ?? []);
      setStats({ total: r.total, online: r.online, offline: r.offline, unassigned: r.unassigned });
    } catch (e: any) { setErr(e?.message ?? 'fetch failed'); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const filtered = (rows ?? []).filter((d) => !filter || JSON.stringify(d).toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-base font-semibold">Devices (Webex Hub)</div>
          <div className="text-xs text-gray-500">
            Live from <code className="font-mono">/v1/devices</code>. Scope verified: <code className="font-mono">spark-admin:devices_read</code>.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter devices…"
                 className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64" />
          <button onClick={load} disabled={busy} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
          {stats && (rows ?? []).length > 0 && (
            <button onClick={() => downloadCsv('webex-hub-devices.csv', filtered)}
                    className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
          )}
        </div>
      </div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <LiveTile label="Total" val={stats.total} err={null} />
          <LiveTile label="Online" val={stats.online} err={null} />
          <LiveTile label="Offline" val={stats.offline} err={null} />
          <LiveTile label="Unassigned" val={stats.unassigned} err={null} />
        </div>
      )}
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      <div className="card overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>{['Display name','Product','Type','Connection','MAC','Workspace/User','IP'].map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold">{c}</th>)}</tr>
          </thead>
          <tbody>
            {(filtered ?? []).slice(0, 100).map((d, i) => (
              <tr key={d.id ?? i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                <td className="px-3 py-1.5">{d.displayName ?? d.name ?? '—'}</td>
                <td className="px-3 py-1.5">{d.product ?? '—'}</td>
                <td className="px-3 py-1.5">{d.type ?? '—'}</td>
                <td className="px-3 py-1.5"><span className={`pill text-[10px] px-2 py-0.5 rounded-full ${
                  /connected/i.test(d.connectionStatus ?? '') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>{d.connectionStatus ?? 'unknown'}</span></td>
                <td className="px-3 py-1.5 font-mono">{d.mac ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-[10px]">{(d.workspaceId ?? d.personId ?? '—').slice(0, 18)}</td>
                <td className="px-3 py-1.5 font-mono">{d.ip ?? '—'}</td>
              </tr>
            ))}
            {!busy && filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">No devices.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── D. Existing WxCC sample-driven report (kept) ────────────────────────────
function WxccSampleReportPanel() {
  const [sample, setSample] = useState('');
  const [data, setData] = useState<WxccSampleReportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      setErr('Only CSV sample files are supported here. Save your XLSX as CSV (UTF-8) and re-upload.'); return;
    }
    setSample(await f.text());
    setErr(null);
  };

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.reportsWxccSample(sample.trim());
      setData(r);
    } catch (e: any) { setErr(e?.message ?? 'parse failed'); setData(null); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500">
        Upload a sample CSV (column headers + any rows). We parse the headers and map each column to a live WxCC entity
        the Service App can read (queues, agents, sites, skills, entry-points, dial numbers). Generated rows have the
        SAME column layout. Columns that can't be mapped are marked unmapped — never invented. <strong>WxCC Analyzer
        (per-call metrics like handle time, abandons) is NOT exposed to Service App scopes</strong> — that's a probe-
        verified Cisco limitation.
      </div>
      <div className="card p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile}
            className="text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-tnh-accent file:text-white file:font-medium file:cursor-pointer" />
          <button onClick={run} disabled={busy || !sample.trim()}
            className="px-4 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? 'Mapping…' : 'Generate report'}
          </button>
        </div>
        <textarea value={sample} onChange={(e) => { setSample(e.target.value); setData(null); setErr(null); }}
          rows={4} placeholder="… or paste CSV here (first line = headers)"
          className="w-full px-3 py-2 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono" />
      </div>
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {data && (
        <>
          <div className="card p-3 space-y-1">
            <div className="text-sm font-semibold">Column mapping</div>
            <ul className="text-xs space-y-0.5">
              {data.column_mapping.map((m) => (
                <li key={m.header}>
                  <code className="font-mono">{m.header}</code> →{' '}
                  {m.mapped
                    ? <span className="text-emerald-700 dark:text-emerald-300">{m.entity}.{m.field} ({m.label})</span>
                    : <span className="text-rose-700 dark:text-rose-300">unmapped — emits "—"</span>}
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-gray-500 mt-1">
              Primary entity: <strong>{data.primary_entity ?? 'none'}</strong> · {data.row_count} rows generated.
            </div>
            {Object.keys(data.entity_errors).length > 0 && (
              <div className="text-[11px] text-rose-600">
                Entity fetch errors: {Object.entries(data.entity_errors).map(([k, v]) => `${k}: ${v}`).join(' · ')}
              </div>
            )}
          </div>
          {data.rows.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
                <div className="text-sm font-semibold">Generated report ({data.rows.length} rows)</div>
                <button onClick={() => downloadCsv('wxcc-sample-report.csv', data.rows)}
                  className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                    <tr>{data.sample_columns.map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold whitespace-nowrap">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {data.rows.slice(0, 200).map((r, i) => (
                      <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                        {data.sample_columns.map((c) => <td key={c} className="px-3 py-1.5">{r[c] ?? '—'}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="text-[11px] text-gray-500">{data.note}</div>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// #3 / #4 — CUCM + UCCX (system CDR from cdr_events)
// ===========================================================================
function SystemReportTab({ system, tenantId }: { system: 'cucm' | 'uccx'; tenantId: string }) {
  const [from, setFrom] = useState(isoLocal(Date.now() - 7 * 86400 * 1000));
  const [to, setTo]     = useState(isoLocal(Date.now()));
  const [data, setData] = useState<SystemCdrReportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.reportsSystemCdr(system, tenantId, {
        from: new Date(from).toISOString(),
        to:   new Date(to).toISOString(),
      });
      setData(r);
    } catch (e: any) { setErr(e?.message ?? 'fetch failed'); } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenantId, system]);

  const label = system === 'cucm' ? 'CUCM' : 'UCCX';
  return (
    <div className="space-y-6">
      {/* For CUCM only — additionally render the 10-report catalogue (port of
          the cucm-axl-tool reference). UCCX has no spoke-side report set yet
          and stays honest empty. */}
      {system === 'cucm' && <CucmCdrUploadPanel tenantId={tenantId} />}
      {system === 'cucm' && <CucmReportsCatalogue tenantId={tenantId} />}

      <div className="space-y-3">
        <div className="text-base font-semibold">{label} CDR</div>
        <div className="text-sm text-gray-500">
          Live from the unified <code className="font-mono">cdr_events</code> table where <code className="font-mono">source_system='{system}'</code>.
          Rows are pushed by the on-prem spoke via <code className="font-mono">/api/spokes/cdr/batch</code>.
          {' '}Empty result here means the {label} CDR collector on the spoke is disabled or unreachable — not that the page is broken.
        </div>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={run} busy={busy} />
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {data && !data.reachable && (
        <div className="card p-4 border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 text-sm text-amber-800 dark:text-amber-200">
          {data.note}
        </div>
      )}
      {data && data.reachable && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { l: 'Total calls',  v: data.totals.total_calls   ?? 0 },
              { l: 'Inbound',      v: data.totals.inbound       ?? 0 },
              { l: 'Outbound',     v: data.totals.outbound      ?? 0 },
              { l: 'Completed',    v: data.totals.completed     ?? 0 },
              { l: 'Avg duration', v: `${data.totals.avg_duration ?? 0}s` },
            ].map((k) => (
              <div key={k.l} className="card p-3">
                <div className="text-2xl font-semibold">{String(k.v)}</div>
                <div className="text-[11px] text-gray-500">{k.l}</div>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            <CountTable title="By day"          rows={data.daily_series} cols={['day', 'calls', 'duration']} />
            <CountTable title="By extension"    rows={data.by_extension} cols={['extension', 'calls', 'duration']} />
            <CountTable title={system === 'uccx' ? 'By CSQ (called number)' : 'By destination'} rows={data.by_destination} cols={['destination', 'calls', 'avg_duration']} />
          </div>
        </>
      )}
      </div>
    </div>
  );
}

// CUCM CDR upload — admins paste the daily billing CSV (AXL does NOT serve
// CDR). Hub-side parser → cdr_events with source_system='cucm'.
function CucmCdrUploadPanel({ tenantId }: { tenantId: string }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ rows_total: number; rows_inserted: number; rows_duplicate: number; rows_skipped: number; note: string } | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsv(await f.text());
    setErr(null); setResult(null);
  };
  const submit = async () => {
    if (!csv.trim()) { setErr('Paste or upload a CUCM CDR CSV first.'); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await api.cucmCdrUpload(csv, tenantId !== 'GLOBAL' ? tenantId : undefined);
      setResult(r);
    } catch (e: any) { setErr(e?.message ?? 'upload failed'); } finally { setBusy(false); }
  };

  return (
    <div className="card p-5 space-y-3">
      <div>
        <div className="text-base font-semibold">CUCM CDR upload (billing-SFTP CSV)</div>
        <div className="text-xs text-gray-500">
          AXL doesn't serve CDR — paste / upload the daily CUCM billing-SFTP CSV. Parser ingests into{' '}
          <code className="font-mono">cdr_events</code> with <code className="font-mono">source_system='cucm'</code>;
          re-uploads of the same CSV are idempotent (INSERT OR IGNORE on globalCallID).
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile}
               className="text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-tnh-accent file:text-white file:font-medium file:cursor-pointer" />
        <button onClick={submit} disabled={busy || !csv.trim()}
                className="px-3 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? 'Ingesting…' : 'Ingest CDR rows'}
        </button>
        {result && <span className="text-xs text-emerald-700 dark:text-emerald-300">{result.rows_inserted} inserted · {result.rows_duplicate} duplicate · {result.rows_skipped} skipped (of {result.rows_total} parsed)</span>}
      </div>
      <textarea value={csv} onChange={(e) => { setCsv(e.target.value); setResult(null); setErr(null); }}
        rows={5} placeholder="… or paste the CUCM CDR CSV here (first line = header)"
        className="w-full px-3 py-2 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono" />
      {err && <div className="text-xs text-rose-600 dark:text-rose-300">{err}</div>}
      {result && <div className="text-[11px] text-gray-500">{result.note}</div>}
      <div className="text-[11px] text-gray-500">
        Ingested rows show up in the <strong>CUCM Reports → CDR</strong> view below within seconds, and in the
        unified Dashboard tab.
      </div>
    </div>
  );
}

// CUCM Reports catalogue — port of the cucm-axl-tool reference reports.
// Renders a 10-report picker + searchable/sortable/exportable result table.
function CucmReportsCatalogue({ tenantId }: { tenantId: string }) {
  const [catalogue, setCatalogue] = useState<Array<{ key: string; title: string; columns: string[]; headers: string[]; description: string }> | null>(null);
  const [pick, setPick] = useState<string>('end_users');
  const [data, setData] = useState<{ report: { key: string; columns: string[]; headers: string[]; description: string }; rows: any[]; total: number; note: string } | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.cucmReportsList().then((r) => setCatalogue(r.reports ?? null)).catch((e) => setErr(e?.message ?? 'catalogue load failed'));
  }, []);

  const run = async () => {
    setBusy(true); setErr(null); setData(null);
    try {
      const r = await api.cucmReport(pick, tenantId);
      setData({ report: r.report, rows: r.rows, total: r.total, note: r.note });
    } catch (e: any) { setErr(e?.message ?? 'report fetch failed'); } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pick, tenantId]);

  const filtered = (data?.rows ?? []).filter((r) =>
    !filter || Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div className="card p-5 space-y-3">
      <div>
        <div className="text-base font-semibold">CUCM Reports — catalogue</div>
        <div className="text-xs text-gray-500">
          Ported from the cucm-axl-tool reference. Tables read from the spoke-pushed cucm_* D1 tables; spoke AXL sync keeps them fresh.
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={!catalogue}
                className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm min-w-[18rem]">
          {(catalogue ?? []).map((r) => <option key={r.key} value={r.key}>{r.title}</option>)}
        </select>
        <button onClick={run} disabled={busy || !catalogue} className="btn-ghost text-sm disabled:opacity-50">{busy ? 'Running…' : '↻ Run'}</button>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter rows…"
               className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64" />
        {data && filtered.length > 0 && (
          <button onClick={() => downloadCsv(`cucm-${data.report.key}.csv`, filtered)}
                  className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
        )}
        <span className="text-xs text-gray-500 ml-auto">{data ? `${filtered.length}${filter ? ` of ${data.total}` : ''} rows` : ''}</span>
      </div>
      {data?.report.description && <div className="text-xs text-gray-500">{data.report.description}</div>}
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {data && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto max-h-[28rem]">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                <tr>{data.report.headers.map((h) => <th key={h} className="text-left px-3 py-1.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={data.report.headers.length} className="px-3 py-8 text-center text-sm text-gray-500">{data.note}</td></tr>}
                {filtered.slice(0, 500).map((r, i) => (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                    {data.report.columns.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap">{r[c] == null ? '—' : String(r[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {data?.note && <div className="text-[11px] text-gray-500">{data.note}</div>}
    </div>
  );
}

// ===========================================================================
// #5 — LGW Reports (per-trunk volume)
// ===========================================================================
function LgwReportTab() {
  const [from, setFrom] = useState(isoLocal(Date.now() - 7 * 86400 * 1000));
  const [to, setTo]     = useState(isoLocal(Date.now()));
  const [data, setData] = useState<LgwCallVolumeResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.reportsLgwVolume({
        from_date: new Date(from).toISOString(),
        to_date:   new Date(to).toISOString(),
      });
      setData(r);
    } catch (e: any) { setErr(e?.message ?? 'fetch failed'); } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500">
        Call volume per Local Gateway trunk, grouped from Webex Detailed Call History. Same scope dependency as
        WxC Report. The trunk list comes from /v1/telephony/config/premisePstn/trunks and is live regardless.
      </div>
      <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={run} busy={busy} />
      {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}
      {data && !data.reachable && (
        <>
          <ProbeTrailBlock probes={data.probes} message={data.message} />
          <div className="card overflow-hidden">
            <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
              <div className="text-sm font-semibold">Trunks registered (live, no CDR)</div>
              {data.trunks.length > 0 && (
                <button onClick={() => downloadCsv('lgw-trunks.csv', data.trunks)}
                  className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
              )}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40"><tr>
                {['Name', 'Type', 'Location', 'ID'].map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold">{c}</th>)}
              </tr></thead>
              <tbody>
                {data.trunks.map((t, i) => (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                    <td className="px-3 py-1.5">{t.name ?? t.trunk_name ?? '—'}</td>
                    <td className="px-3 py-1.5">{t.type ?? t.trunk_type ?? '—'}</td>
                    <td className="px-3 py-1.5">{t.location ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">{(t.id ?? t.trunk_id ?? '').slice(0, 18)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {data && data.reachable && (
        <div className="card overflow-hidden">
          <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
            <div className="text-sm font-semibold">Per-LGW call volume — {data.window.from.slice(0, 10)} → {data.window.to.slice(0, 10)}</div>
            {data.trunks.length > 0 && (
              <button onClick={() => downloadCsv('lgw-call-volume.csv', data.trunks)}
                className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40"><tr>
              {['Trunk', 'Type', 'Location', 'Calls', 'In', 'Out', 'Total dur (s)', 'Avg dur (s)'].map((c) => <th key={c} className="text-left px-3 py-1.5 font-semibold">{c}</th>)}
            </tr></thead>
            <tbody>
              {data.trunks.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">No CDR rows in this window.</td></tr>}
              {data.trunks.map((t, i) => (
                <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                  <td className="px-3 py-1.5">{t.trunk_name ?? t.name}</td>
                  <td className="px-3 py-1.5">{t.trunk_type ?? t.type ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.location ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.calls ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.inbound ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.outbound ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.total_duration_sec ?? '—'}</td>
                  <td className="px-3 py-1.5">{t.avg_duration_sec ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.unmatched_trunks && data.unmatched_trunks.length > 0 && (
            <div className="px-4 py-2 text-[11px] text-gray-500 border-t border-tnh-border dark:border-tnh-border-dark">
              {data.unmatched_trunks.length} trunk(s) had no calls in this window:{' '}
              {data.unmatched_trunks.map((t) => t.name).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Dashboard tab — KPI cards + trend SVG + top callers/destinations
// ===========================================================================

function num(n?: number | null) { return n == null ? '—' : n.toLocaleString(); }
function fmtSec(s?: number | null) { if (s == null) return '—'; if (s < 60) return `${Math.round(s)}s`; return `${Math.floor(s/60)}m ${Math.round(s%60)}s`; }
function fmtTotalDuration(s?: number | null) { if (s == null) return '—'; const h = Math.floor(s/3600); const m = Math.floor((s%3600)/60); if (h > 0) return `${h}h ${m}m`; return `${m}m`; }

function DashboardTab({ me, tenantId }: { me: Me; tenantId: string }) {
  const [data, setData] = useState<ReportDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [range, setRange] = useState<'1d' | '7d' | '30d' | '90d'>('7d');
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setErr(null); const r = await api.reportsDashboard(tenantId, range); setData(r); }
    catch (e: any) { setErr(e.message); setData(null); }
  }
  useEffect(() => { load(); }, [tenantId, range]);

  async function seed() {
    if (!confirm(`Seed 30 days of mock CDR for tenant "${tenantId}"? (Super-admin demo helper — does NOT affect real spoke data.)`)) return;
    setBusy(true);
    try { const r = await api.reportsSeed(tenantId, 30); alert(`Seeded ${r.inserted} mock CDR rows.`); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const totals = data?.totals ?? {};
  const callsBucket = totals.total_calls ?? 0;
  const completionPct = (totals.total_calls && totals.completed != null) ? Math.round((totals.completed / totals.total_calls) * 100) : null;
  const abandonPct = (totals.total_calls && totals.abandoned != null) ? Math.round((totals.abandoned / totals.total_calls) * 100) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {(['1d','7d','30d','90d'] as const).map((r) => (
          <button key={r} onClick={() => setRange(r)}
                  className={`px-3 py-1 rounded-md text-xs ${range === r ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>{r}</button>
        ))}
        <div className="flex-1" />
        {me.role === 'super_admin' && callsBucket === 0 && (
          <button onClick={seed} disabled={busy} className="btn-ghost text-xs text-tnh-accent">{busy ? 'Seeding…' : 'Seed mock data (super-admin)'}</button>
        )}
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      {data === null && !err && <div className="card p-8 text-center text-gray-500">Loading…</div>}

      {data && (
        <>
          {callsBucket === 0 && (
            <div className="card p-6 text-center text-gray-500">
              <div className="font-medium">No call data yet for this tenant.</div>
              <div className="text-sm mt-1">
                Once the spoke is connected (Milestone 8) CUCM/UCCX/WxC CDRs will flow in here automatically.
                {me.role === 'super_admin' && ' For now, click Seed mock data above to populate a 30-day demo.'}
              </div>
            </div>
          )}

          <section className="grid gap-3 grid-cols-2 md:grid-cols-4">
            <Kpi label="Total calls" value={num(callsBucket)} sub={`${range} window`} />
            <Kpi label="Completion rate" value={completionPct == null ? '—' : `${completionPct}%`} sub={`${num(totals.completed)} completed`} />
            <Kpi label="Abandon rate" value={abandonPct == null ? '—' : `${abandonPct}%`} sub={`${num(totals.abandoned)} abandoned`} severity={abandonPct != null && abandonPct >= 15 ? 'warn' : 'ok'} />
            <Kpi label="Total talk time" value={fmtTotalDuration(totals.total_duration)} sub={`avg ${fmtSec(totals.avg_duration)}`} />
            <Kpi label="Inbound" value={num(totals.inbound)} />
            <Kpi label="Outbound" value={num(totals.outbound)} />
            <Kpi label="Internal" value={num(totals.internal)} />
            <Kpi label="Avg MOS" value={totals.avg_mos == null ? '—' : String(totals.avg_mos)} sub={totals.avg_mos != null && totals.avg_mos < 4.0 ? 'check call quality' : 'good'} severity={totals.avg_mos != null && totals.avg_mos < 4.0 ? 'warn' : 'ok'} />
          </section>

          {data.daily_series.length > 0 && <TrendChart series={data.daily_series} />}

          <div className="grid gap-3 md:grid-cols-2">
            <div className="card p-5">
              <div className="text-sm font-semibold mb-3">Top callers</div>
              {(data.top_callers ?? []).length === 0 && <div className="text-sm text-gray-500">No data</div>}
              {(data.top_callers ?? []).map((c) => (
                <div key={c.calling_number} className="flex justify-between text-sm py-1.5 border-b last:border-b-0 border-tnh-border/50 dark:border-tnh-border-dark/50">
                  <span className="font-mono">{c.calling_number}</span>
                  <span className="text-gray-500">{c.calls} calls</span>
                </div>
              ))}
            </div>
            <div className="card p-5">
              <div className="text-sm font-semibold mb-3">Top destinations</div>
              {(data.top_destinations ?? []).length === 0 && <div className="text-sm text-gray-500">No data</div>}
              {(data.top_destinations ?? []).map((c) => (
                <div key={c.called_number} className="flex justify-between text-sm py-1.5 border-b last:border-b-0 border-tnh-border/50 dark:border-tnh-border-dark/50">
                  <span className="font-mono">{c.called_number}</span>
                  <span className="text-gray-500">{c.calls} calls</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, severity }: { label: string; value: string; sub?: string; severity?: 'ok' | 'warn' | 'err' }) {
  const col = severity === 'warn' ? 'text-tnh-warn' : severity === 'err' ? 'text-tnh-err' : '';
  return (
    <div className="card p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${col}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function TrendChart({ series }: { series: Array<{ day: string; calls: number; completed: number; abandoned: number }> }) {
  const W = 720, H = 180, P = 24;
  const max = Math.max(1, ...series.map((d) => d.calls));
  const xStep = (W - 2 * P) / Math.max(1, series.length - 1);
  const points = series.map((d, i) => `${P + i * xStep},${H - P - (d.calls / max) * (H - 2 * P)}`);
  const barW = Math.max(2, (W - 2 * P) / series.length - 4);
  return (
    <div className="card p-5">
      <div className="text-sm font-semibold mb-3">Daily call volume</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="currentColor" strokeOpacity="0.2" />
        {series.map((d, i) => {
          const x = P + i * xStep;
          const completedH = (d.completed / max) * (H - 2 * P);
          const abandonedH = (d.abandoned / max) * (H - 2 * P);
          return (
            <g key={d.day}>
              <rect x={x - barW / 2} y={H - P - completedH} width={barW} height={completedH} fill="#0064ff" opacity="0.8" />
              <rect x={x - barW / 2} y={H - P - completedH - abandonedH} width={barW} height={abandonedH} fill="#b00020" opacity="0.6" />
            </g>
          );
        })}
        <polyline points={points.join(' ')} fill="none" stroke="#1f8a4d" strokeWidth="1.5" />
      </svg>
      <div className="flex gap-4 text-xs text-gray-500 mt-2">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-tnh-accent opacity-80"></span> completed</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-tnh-err opacity-60"></span> abandoned</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-tnh-ok opacity-80 rounded-full"></span> total trend</span>
      </div>
    </div>
  );
}

// ===========================================================================
// CDR tab — paginated table with filters
// ===========================================================================

function CdrTab({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<CdrRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [direction, setDirection] = useState<string>('');
  const [disposition, setDisposition] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setErr(null);
      const r = await api.reportsCdr(tenantId, { limit, offset, direction: direction || undefined, disposition: disposition || undefined });
      setRows(r.cdrs); setTotal(r.total);
    } catch (e: any) { setErr(e.message); setRows([]); setTotal(0); }
  }
  useEffect(() => { load(); }, [tenantId, offset, direction, disposition]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-gray-500">Direction:</span>
        {['', 'inbound', 'outbound', 'internal'].map((d) => (
          <button key={d || 'all'} onClick={() => { setDirection(d); setOffset(0); }}
                  className={`px-3 py-1 rounded-md ${direction === d ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
            {d || 'all'}
          </button>
        ))}
        <span className="text-gray-500 ml-3">Disposition:</span>
        {['', 'completed', 'abandoned', 'no_answer', 'busy'].map((d) => (
          <button key={d || 'all'} onClick={() => { setDisposition(d); setOffset(0); }}
                  className={`px-3 py-1 rounded-md ${disposition === d ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
            {d || 'all'}
          </button>
        ))}
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase tracking-wide">
            <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Dir</th>
              <th className="text-left px-4 py-2">From</th>
              <th className="text-left px-4 py-2">To</th>
              <th className="text-right px-4 py-2">Ring</th>
              <th className="text-right px-4 py-2">Duration</th>
              <th className="text-left px-4 py-2">Outcome</th>
              <th className="text-right px-4 py-2">MOS</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-gray-500 py-8">No CDR rows for the current filter.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.cdr_id} className="border-b last:border-b-0 border-tnh-border/40 dark:border-tnh-border-dark/40 hover:bg-tnh-accent/[0.02]">
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{new Date(r.setup_ts).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs"><span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{r.direction}</span></td>
                <td className="px-4 py-2 font-mono text-xs">{r.calling_number}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.called_number}</td>
                <td className="px-4 py-2 text-right text-xs">{r.ring_seconds}s</td>
                <td className="px-4 py-2 text-right text-xs">{fmtSec(r.duration_seconds)}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={`pill ${r.disposition === 'completed' ? 'pill-ok' : r.disposition === 'abandoned' ? 'pill-err' : 'pill-warn'}`}>{r.disposition}</span>
                </td>
                <td className="px-4 py-2 text-right text-xs">{r.mos_score ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center text-sm">
        <div className="text-gray-500">{rows.length === 0 ? 'no rows' : `showing ${offset + 1}–${offset + rows.length} of ${total}`}</div>
        <div className="flex gap-2">
          <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="btn-ghost text-sm">← Prev</button>
          <button onClick={() => setOffset(offset + limit)} disabled={offset + rows.length >= total} className="btn-ghost text-sm">Next →</button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Runs tab — on-demand exports
// ===========================================================================

function RunsTab({ me, tenantId }: { me: Me; tenantId: string }) {
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try { setErr(null); const r = await api.reportsRunList(me.role === 'super_admin' ? tenantId : undefined); setRuns(r.runs); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); const t = setInterval(load, 10_000); return () => clearInterval(t); }, [tenantId, me.role]);

  async function newRun() {
    setBusy(true);
    try {
      const days = parseInt(prompt('Export how many days of CDR? (1-30)', '7') ?? '0', 10);
      if (!days || days < 1 || days > 30) { setBusy(false); return; }
      const to = new Date().toISOString();
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      await api.reportsRunCreate({ tenant_id: tenantId, report_type: 'cdr_export', format: 'csv', params: { from, to } });
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start">
        <p className="text-sm text-gray-500">On-demand CDR exports. Files land in R2 and stream when you click Download.</p>
        <button onClick={newRun} disabled={busy} className="btn-primary text-sm">{busy ? 'Queuing…' : '+ New export'}</button>
      </div>
      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase tracking-wide">
            <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              <th className="text-left px-4 py-2">Requested</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Format</th>
              <th className="text-left px-4 py-2">By</th>
              <th className="text-right px-4 py-2">Rows</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-8">No runs yet.</td></tr>}
            {runs.map((r) => (
              <tr key={r.run_id} className="border-b last:border-b-0 border-tnh-border/40 dark:border-tnh-border-dark/40">
                <td className="px-4 py-2 text-xs whitespace-nowrap">{new Date(r.requested_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs">{r.report_type}</td>
                <td className="px-4 py-2 text-xs uppercase">{r.format}</td>
                <td className="px-4 py-2 text-xs">{r.requested_by_email}</td>
                <td className="px-4 py-2 text-right text-xs">{r.result_row_count ?? '—'}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={`pill ${r.status === 'completed' ? 'pill-ok' : r.status === 'failed' ? 'pill-err' : 'pill-warn'}`}>{r.status}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  {r.status === 'completed' ? (
                    <a href={api.reportsRunDownloadUrl(r.run_id)} className="text-xs text-tnh-accent hover:underline">Download ↓</a>
                  ) : <span className="text-xs text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Schedules tab — recurring auto-email reports
// ===========================================================================

function SchedulesTab({ me, tenantId }: { me: Me; tenantId: string }) {
  const [list, setList] = useState<ScheduledReport[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try { setErr(null); const r = await api.reportsScheduleList(me.role === 'super_admin' ? tenantId : undefined); setList(r.schedules); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, [tenantId, me.role]);

  async function toggle(s: ScheduledReport) {
    setBusy(true);
    try { await api.reportsScheduleUpdate(s.schedule_id, { enabled: s.enabled === 0 }); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function remove(s: ScheduledReport) {
    if (!confirm(`Delete schedule "${s.name}"?`)) return;
    setBusy(true);
    try { await api.reportsScheduleDelete(s.schedule_id); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function submitNew(form: { name: string; cadence: any; time_of_day: string; day_of_week?: number; day_of_month?: number; emails: string }) {
    setBusy(true);
    try {
      await api.reportsScheduleCreate({
        name: form.name,
        report_type: 'cdr_export',
        format: 'csv',
        cadence: form.cadence,
        time_of_day: form.time_of_day,
        day_of_week: form.day_of_week,
        day_of_month: form.day_of_month,
        delivery_emails: form.emails.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setShowNew(false);
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start">
        <p className="text-sm text-gray-500">Recurring report deliveries. Cron picks up due schedules every 15 min, runs the export, and emails the download link.</p>
        <button onClick={() => setShowNew(true)} className="btn-primary text-sm">+ New schedule</button>
      </div>
      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
      {showNew && <NewScheduleForm onCancel={() => setShowNew(false)} onSubmit={submitNew} busy={busy} />}

      <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
        {list.length === 0 && <div className="p-8 text-center text-gray-500 text-sm">No scheduled reports.</div>}
        {list.map((s) => {
          const emails = (() => { try { return JSON.parse(s.delivery_emails_json) as string[]; } catch { return []; } })();
          return (
            <div key={s.schedule_id} className="p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{s.name}</span>
                  <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{s.cadence}</span>
                  <span className="text-xs text-gray-500">@ {s.time_of_day} {s.cadence === 'weekly' && `· day ${s.day_of_week}`}{s.cadence === 'monthly' && `· day ${s.day_of_month}`}</span>
                  <span className={`pill ${s.enabled === 1 ? 'pill-ok' : 'pill-warn'}`}>{s.enabled === 1 ? 'enabled' : 'paused'}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {s.report_type} · {s.format.toUpperCase()} · to {emails.join(', ') || '(none)'}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  next: {new Date(s.next_run_at).toLocaleString()}
                  {s.last_run_at && ` · last: ${new Date(s.last_run_at).toLocaleString()} (${s.last_run_status})`}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(s)} disabled={busy} className="btn-ghost text-xs">{s.enabled === 1 ? 'Pause' : 'Resume'}</button>
                <button onClick={() => remove(s)} disabled={busy} className="btn-ghost text-xs text-tnh-err">Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewScheduleForm({ onCancel, onSubmit, busy }: { onCancel: () => void; onSubmit: (f: any) => void; busy: boolean }) {
  const [name, setName] = useState('Daily CDR digest');
  const [cadence, setCadence] = useState<'hourly' | 'daily' | 'weekly' | 'monthly'>('daily');
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [emails, setEmails] = useState('ahmed@triple-network.com');

  function handle(e: FormEvent) {
    e.preventDefault();
    onSubmit({ name, cadence, time_of_day: timeOfDay,
      day_of_week: cadence === 'weekly' ? dayOfWeek : undefined,
      day_of_month: cadence === 'monthly' ? dayOfMonth : undefined,
      emails });
  }
  return (
    <form onSubmit={handle} className="card p-5 space-y-3 text-sm">
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cadence</label>
          <select value={cadence} onChange={(e) => setCadence(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {cadence !== 'hourly' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Time of day (UTC)</label>
            <input type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
        )}
        {cadence === 'weekly' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Day of week</label>
            <select value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((n, i) => <option key={i} value={i}>{n}</option>)}
            </select>
          </div>
        )}
        {cadence === 'monthly' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Day of month (1–28)</label>
            <input type="number" min={1} max={28} value={dayOfMonth} onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10) || 1)}
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
        )}
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Recipients (comma-separated)</label>
          <input value={emails} onChange={(e) => setEmails(e.target.value)}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent"
                 placeholder="ops@example.com, admin@example.com" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
        <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Saving…' : 'Create schedule'}</button>
      </div>
    </form>
  );
}

// ===========================================================================
// #6 — Reports Template (BUILD-TASK-reporting v3 §6)
// Upload Cisco WxCC Analyzer JSON definitions, browse, apply to any team/CSQ.
// 7 seed templates ship under apps/spa/public/seed-templates/ and are
// uploaded via the same POST endpoint as ad-hoc uploads. Renamed to drop
// the team-specific "TSB " prefix in BUILD-TASK-rename-templates-strip-tsb.
// ===========================================================================
const SEED_TEMPLATE_FILES = [
  'Agent Available.json',
  'Team RT.json',
  'Call Queue.json',
  'Agent Login_Logout Activity.json',
  'Agent Summary Report.json',
  'Agent State History.json',
  'CSQ All Fields Report.json',
];

function ReportsTemplateTab({ tenantId: _tenantId }: { tenantId: string }) {
  const [templates, setTemplates] = useState<WxccReportTemplate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [picked, setPicked] = useState<WxccReportTemplate | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.wxccTemplateList();
      setTemplates(r.templates ?? []);
    } catch (e: any) { setErr(e?.message ?? 'list failed'); } finally { setBusy(false); }
  };
  useEffect(() => { refresh(); }, []);

  const uploadOne = async (json: any, source: string): Promise<void> => {
    try {
      const r = await api.wxccTemplateUpload(json);
      setMsg(`Uploaded "${r.name}" (${r.scope}/${r.profile}, ${r.column_count} columns) from ${source}.`);
    } catch (e: any) {
      setErr(`Upload from ${source} failed: ${e?.message ?? e}`);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setMsg(null); setErr(null);
    const files = Array.from(e.target.files ?? []);
    for (const f of files) {
      try {
        const text = await f.text();
        const json = JSON.parse(text);
        await uploadOne(json, f.name);
      } catch (parseErr: any) {
        setErr(`${f.name}: ${parseErr?.message ?? parseErr}`);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
    await refresh();
  };

  const seedAll = async () => {
    setBusy(true); setMsg(null); setErr(null);
    let ok = 0, failed = 0;
    for (const name of SEED_TEMPLATE_FILES) {
      try {
        const res = await fetch(`/seed-templates/${encodeURIComponent(name)}`);
        if (!res.ok) { failed++; continue; }
        const json = await res.json();
        await api.wxccTemplateUpload(json);
        ok++;
      } catch { failed++; }
    }
    setBusy(false);
    setMsg(`Seeded ${ok}/${SEED_TEMPLATE_FILES.length} report templates${failed ? ` (${failed} failed)` : ''}.`);
    await refresh();
  };

  const onDelete = async (id: string, name: string) => {
    if (!confirm(`Delete template "${name}"?`)) return;
    setBusy(true); setMsg(null); setErr(null);
    try { await api.wxccTemplateDelete(id); setMsg(`Deleted "${name}".`); }
    catch (e: any) { setErr(e?.message ?? 'delete failed'); }
    finally { setBusy(false); await refresh(); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-3">
        <div>
          <div className="text-base font-semibold">Reports Template</div>
          <p className="text-sm text-gray-500 max-w-3xl">
            Upload Cisco WxCC Analyzer report definitions (JSON, schema <code className="font-mono">v3.0</code>).
            Browse stored templates and apply any one to a chosen team or CSQ — the original team/CSQ filter is
            substituted at run time. HISTORICAL templates execute against <code className="font-mono">/organization/&#123;orgId&#125;/task</code>;
            REALTIME templates require a streaming endpoint not exposed to this Service App.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".json,application/json" multiple onChange={onFile}
                 className="text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-tnh-accent file:text-white file:font-medium file:cursor-pointer" />
          <button onClick={seedAll} disabled={busy}
                  className="px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-sm font-medium hover:opacity-90 disabled:opacity-50">
            ⤓ Seed default templates (7)
          </button>
          <button onClick={refresh} disabled={busy} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
          {msg && <span className="text-xs text-emerald-700 dark:text-emerald-300">{msg}</span>}
          {err && <span className="text-xs text-rose-600 dark:text-rose-300">{err}</span>}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
          <div className="text-sm font-semibold">Templates</div>
          <span className="text-xs text-gray-500">{templates ? `${templates.length} stored` : 'loading…'}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>{['Name','Scope','Profile','Columns','Original filter','Uploaded','Actions'].map((c) => <th key={c} className="text-left px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{c}</th>)}</tr>
          </thead>
          <tbody>
            {(templates ?? []).length === 0 && !busy && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No templates uploaded yet — click <strong>⤓ Seed default templates</strong> to load the 7 packaged Analyzer reports, or pick a .json file above.</td></tr>
            )}
            {(templates ?? []).map((t) => (
              <tr key={t.template_id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <td className="px-3 py-2"><div className="font-medium">{t.name}</div>{t.title && <div className="text-[11px] text-gray-500">{t.title}</div>}</td>
                <td className="px-3 py-2 text-xs"><span className={`pill text-[10px] px-2 py-0.5 rounded-full ${t.scope === 'HISTORICAL' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>{t.scope || '—'}</span></td>
                <td className="px-3 py-2 text-xs">{t.profile || '—'}</td>
                <td className="px-3 py-2 text-xs">{t.columns?.length ?? 0}</td>
                <td className="px-3 py-2 text-xs font-mono">{t.original_filter || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{t.uploaded_at ? new Date(t.uploaded_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-xs">
                  <button onClick={() => setPicked(t)} className="px-2.5 py-1 rounded-md bg-tnh-accent text-white text-[11px] font-medium mr-1.5">Run →</button>
                  <button onClick={() => onDelete(t.template_id, t.name)} className="px-2.5 py-1 rounded-md border border-tnh-border dark:border-tnh-border-dark text-[11px] font-medium hover:bg-rose-50 dark:hover:bg-rose-900/30">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {picked && <RunTemplateDialog template={picked} onClose={() => setPicked(null)} />}
    </div>
  );
}

function RunTemplateDialog({ template, onClose }: { template: WxccReportTemplate; onClose: () => void }) {
  const [targets, setTargets] = useState<{ teams: Array<{ id: string; name: string }>; queues: Array<{ id: string; name: string }> } | null>(null);
  // BUILD-TASK-realtime-target-types-and-queries Step 0 + Step 5 — the
  // template now ships its own target-type metadata (default + allowed
  // list). Default the dropdown to template.default_target_type instead
  // of hard-coding 'team' — fixes Call Queue defaulting to Team (wrong;
  // should be Queue/CSQ).
  const allowedTargetTypes: Array<'team' | 'csq'> =
    (template.allowed_target_types && template.allowed_target_types.length > 0)
      ? template.allowed_target_types
      : ['team', 'csq'];
  const initialTargetType: 'team' | 'csq' =
    template.default_target_type
      ?? (allowedTargetTypes[0] ?? 'team');
  const [targetType, setTargetType] = useState<'team' | 'csq'>(initialTargetType);
  const [targetValue, setTargetValue] = useState<string>('');
  const [from, setFrom] = useState(isoLocal(Date.now() - 7 * 86400 * 1000));
  const [to, setTo]     = useState(isoLocal(Date.now()));
  const [result, setResult] = useState<WxccTemplateRunResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.wxccTeams().catch(() => ({ teams: [] })),
      api.wxccQueues().catch(() => ({ queues: [] })),
    ]).then(([t, q]) => setTargets({
      teams:  (t.teams  ?? []).map((x: any) => ({ id: x.id, name: x.name ?? x.id })),
      queues: (q.queues ?? []).map((x: any) => ({ id: x.id, name: x.name ?? x.id })),
    }));
  }, []);

  const run = async () => {
    if (!targetValue) { setErr('Pick a target.'); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      const body: { target_type: 'team' | 'csq'; target_value: string; from?: number; to?: number } = {
        target_type: targetType, target_value: targetValue,
      };
      if (template.scope === 'HISTORICAL') {
        body.from = new Date(from).getTime();
        body.to   = new Date(to).getTime();
      }
      const r = await api.wxccTemplateRun(template.template_id, body);
      setResult(r);
    } catch (e: any) { setErr(e?.message ?? 'run failed'); } finally { setBusy(false); }
  };

  const visibleCols = (template.columns ?? []).filter((c) => !c.hidden);
  const targetOptions = targetType === 'team' ? (targets?.teams ?? []) : (targets?.queues ?? []);

  return (
    <div className="fixed inset-0 z-40 bg-black/40 grid place-items-start overflow-auto p-6">
      <div className="card p-5 max-w-5xl w-full space-y-3 mt-12">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Run template</div>
            <div className="text-base font-semibold">{template.name}</div>
            <div className="text-xs text-gray-500">{template.scope} · {template.profile} · {visibleCols.length} visible columns · original filter <code className="font-mono">{template.original_filter || '—'}</code></div>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm">× Close</button>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 items-end">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Target type</span>
            {/* BUILD-TASK-realtime-target-types-and-queries Step 5 — when the
                template allows only ONE target type, lock the dropdown.
                "Call Queue" makes no sense scoped to a Team; "Team RT" makes
                no sense scoped to a CSQ. Showing the lone option as a
                non-interactive pill prevents the misclick. */}
            {allowedTargetTypes.length === 1 ? (
              <div
                title={`This template is locked to ${allowedTargetTypes[0] === 'csq' ? 'Queue / CSQ' : 'Team'} — the underlying GraphQL query has no meaningful equivalent for the other target type.`}
                className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-gray-50 dark:bg-gray-900/40 text-sm text-gray-700 dark:text-gray-300 select-none cursor-help"
              >
                {allowedTargetTypes[0] === 'csq' ? 'Queue / CSQ' : 'Team'}
                <span className="ml-1 text-[10px] text-gray-400">(locked)</span>
              </div>
            ) : (
              <select value={targetType} onChange={(e) => { setTargetType(e.target.value as any); setTargetValue(''); }}
                      className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm dark:[color-scheme:dark]">
                {allowedTargetTypes.includes('team') && (
                  <option value="team" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">Team</option>
                )}
                {allowedTargetTypes.includes('csq') && (
                  <option value="csq" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">Queue / CSQ</option>
                )}
              </select>
            )}
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">{targetType === 'team' ? 'Team' : 'Queue'}</span>
            <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)} disabled={!targets}
                    className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm dark:[color-scheme:dark]">
              <option value="" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">— Select —</option>
              {/* Per BUILD-TASK-reports-template-fix Fix 2: substitute by ID,
                  not by name — the option's `value` is the entity ID. */}
              {targetOptions.map((o) => (
                <option key={o.id} value={o.id} className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">{o.name}</option>
              ))}
            </select>
          </label>
          {template.scope === 'HISTORICAL' && (
            <>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">From</span>
                <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)}
                       className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">To</span>
                <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)}
                       className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
              </label>
            </>
          )}
          <button onClick={run} disabled={busy || !targetValue}
                  className="px-4 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? 'Running…' : 'Run'}
          </button>
        </div>

        {err && <div className="card p-3 text-sm text-rose-600">{err}</div>}

        {result && (
          <div className="card overflow-hidden">
            <div className="px-3 py-2 flex items-center justify-between border-b border-tnh-border dark:border-tnh-border-dark flex-wrap gap-2">
              <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                <span>
                  {result.template.name} — {result.target.type === 'team' ? 'Team' : 'CSQ'}{' '}
                  <code className="font-mono">{result.target.name || result.target.value}</code>
                  {result.target.name && <span className="text-gray-400 ml-2 font-mono text-[10px]">(id: {result.target.value.slice(0, 18)}…)</span>}
                  {' '}— {result.total} row(s){typeof result.raw_total === 'number' && result.raw_total !== result.total ? ` of ${result.raw_total}` : ''}
                </span>
                {/* BUILD-TASK-search-api-via-existing-service-app Step 6 — the
                    green "Live · Search API" pill shows the rows came from the
                    Cisco WxCC GraphQL Search API (api.wxcc-us1.cisco.com/search)
                    using the existing Service App token. Replaces the
                    historical-fallback panel entirely for that run. */}
                {result.auth_method === 'wxcc_service_app_search_api' && (
                  <span title={result.search_api_tracking_id ? `Cisco trackingId: ${result.search_api_tracking_id}` : undefined}
                        className="text-[10px] font-normal uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-800">
                    Live · Search API{result.search_api_query_kind ? ` · ${result.search_api_query_kind}` : ''}
                  </span>
                )}
                {result.auth_method === 'webex_control_hub_admin_oauth' && (
                  <span className="text-[10px] font-normal uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border border-blue-300 dark:border-blue-800">
                    Live · Admin OAuth
                  </span>
                )}
              </div>
              {result.rows.length > 0 && (
                <button onClick={() => {
                  // BUILD-TASK-format-durations-and-fix-mappings Fix 7 — CSV
                  // uses RAW values, not the UI-formatted strings, so the
                  // exported file is arithmetic-able in Excel. SPA's UI
                  // formatters apply only in the <td> render path below.
                  const colTypes = result.column_types ?? {};
                  const rows = result.rows.map((r) =>
                    Object.fromEntries(visibleCols.map((c) => {
                      const raw = r[c.id] ?? r[c.name] ?? r[c.alias];
                      return [c.alias, formatCellForCsv(raw, colTypes[c.id] as CellType | undefined)];
                    })),
                  );
                  downloadCsv(`${result.template.name.replace(/[^A-Za-z0-9._-]/g, '_')}-${(result.target.name || result.target.value).replace(/[^A-Za-z0-9._-]/g, '_')}.csv`, rows);
                }} className="text-xs text-tnh-accent hover:underline">↓ CSV</button>
              )}
            </div>
            {/* BUILD-TASK-oauth-diagnose-and-honest-fallback Fix 5 — Cisco
                platform boundary panel. ONLY shown for HISTORICAL templates.
                For REALTIME the panel is wrong — admin OAuth + Analyzer XLSX
                upload are historical-only workflows; live snapshots use the
                /task?channelType=telephony endpoint (or degrade to configured-
                state in `data_kind`). */}
            {result.template.scope === 'HISTORICAL'
              && (result.cisco_platform_boundary || result.requires_control_hub_admin_auth) && (
              <CiscoBoundaryPanel
                templateId={template.template_id}
                templateScope="HISTORICAL"
                analyzerLink={result.analyzer_link}
                targetType={result.target.type}
                targetId={result.target.value}
                targetName={result.target.name ?? result.target.value}
              />
            )}
            {/* REALTIME templates: small inline note describing what kind of
                rows the live snapshot returned. No big panel; the table below
                renders whatever real data we have.
                BUILD-TASK-realtime-target-types-and-queries Step 4 — when the
                GraphQL Search API path (auth_method === wxcc_service_app_search
                _api) succeeded, the green LIVE badge above is enough — we
                drop the redundant inline note so the panel doesn't double-
                announce. The legacy "Live task snapshot endpoint returned
                404 …" copy was correct for the REST path and wrong for the
                GraphQL path; we now branch by query_kind so the wording
                matches what was actually queried. */}
            {result.template.scope === 'REALTIME'
              && result.data_kind
              && result.auth_method !== 'wxcc_service_app_search_api'
              && (
              <div className={`px-4 py-2 text-[11px] border-b ${
                result.data_kind === 'live_snapshot'
                  ? 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                  : result.data_kind === 'configured_state'
                  ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/30 text-amber-800 dark:text-amber-200'
                  : 'bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-900/30 text-rose-800 dark:text-rose-200'
              }`}>
                {result.data_kind === 'live_snapshot'
                  ? `Live task snapshot — ${result.total} active task(s) for this ${result.target.type === 'csq' ? 'queue' : 'team'}.`
                  : result.data_kind === 'configured_state'
                  ? `Live realtime endpoints returned ${result.snapshot_status ?? '?'} on this Service App scope (the Operations Dashboard hits the same wall). Showing configured-state rows for this ${result.target.type === 'csq' ? 'queue' : 'team'}; per-call / per-second telemetry fields render as "—".`
                  : `Realtime endpoints unreachable — see error block below.`}
              </div>
            )}
            {/* Structured Webex error — http status + body + tracking id.
                BUILD-TASK-reports-probe-and-rewire Step 3 — status-class
                copy. Falls back to the string form for older response shapes. */}
            {result.error && (typeof result.error === 'object' ? (() => {
              const st = result.error.http_status;
              const hint =
                st === 401 || st === 403
                  ? 'Likely a missing scope on the Service App — re-issue with the right scopes in Control Hub → My Apps and re-paste the refresh token in Configuration Manager.'
                  : st === 404
                  ? 'Endpoint not found — see the probe table; this template\'s data source isn\'t reachable with the current Service App. Path Cisco re-mapped to is in the response body above.'
                  : (st >= 500 && st < 600)
                  ? 'Webex-side error — retry; if it persists, check the Webex status page (status.webex.com).'
                  : 'Other failure — check the body + tracking ID with Cisco TAC.';
              return (
                <div className="px-4 py-3 space-y-1 text-xs bg-rose-50 dark:bg-rose-900/15 border-b border-rose-200 dark:border-rose-900/30">
                  <div className="font-semibold text-rose-700 dark:text-rose-300">
                    Webex API error — HTTP {st}
                  </div>
                  {result.error.tracking_id && (
                    <div className="text-rose-700 dark:text-rose-300">
                      Tracking ID: <code className="font-mono">{result.error.tracking_id}</code>{' '}
                      <span className="text-rose-600 dark:text-rose-400">(provide this to Cisco TAC)</span>
                    </div>
                  )}
                  <div className="text-rose-700 dark:text-rose-300 italic">{hint}</div>
                  <pre className="font-mono text-[11px] whitespace-pre-wrap bg-white dark:bg-gray-900 p-2 rounded max-h-40 overflow-auto text-gray-800 dark:text-gray-200">{result.error.webex_body || '(empty response body)'}</pre>
                </div>
              );
            })() : (
              <div className="px-4 py-3 text-xs text-rose-600 dark:text-rose-300">{result.error}</div>
            ))}
            {result.note && <div className="px-4 py-2 text-[11px] text-gray-500">{result.note}</div>}
            {result.source && <div className="px-4 py-0 text-[10px] font-mono text-gray-400">source: {result.source}</div>}
            {/* BUILD-TASK-realtime-target-types-and-queries Step 2 — when a
                REALTIME template has just ONE visible column AND that column
                is an aggregate (count/max/min over the rows), render a big
                summary card instead of a 1×1 table. The Call Queue template
                is the canonical case: a single "On Queue" count. */}
            {(() => {
              const isSingleAggregate =
                result.template.scope === 'REALTIME'
                && visibleCols.length === 1
                && result.rows.length > 0;
              if (!isSingleAggregate) return null;
              const c = visibleCols[0]!;
              const r = result.rows[0]!;
              const raw = r[c.id] ?? r[c.name] ?? r[c.alias];
              const type = (result.column_types ?? {})[c.id] as CellType | undefined;
              const formatted = formatCellForDisplay(raw, type);
              return (
                <div className="px-6 py-8 text-center border-b border-tnh-border dark:border-tnh-border-dark">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{c.alias}</div>
                  <div className="text-5xl font-semibold text-tnh-accent">{formatted}</div>
                  <div className="text-[11px] text-gray-500 mt-2">
                    {result.target.type === 'csq' ? 'Queue' : 'Team'}: <code className="font-mono">{result.target.name || result.target.value}</code>
                    {' · '}live as of {new Date().toLocaleTimeString()}
                  </div>
                </div>
              );
            })()}
            {visibleCols.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Template has no visible columns.</div>
            ) : (
              <div className={`overflow-x-auto max-h-[28rem] ${result.template.scope === 'REALTIME' && visibleCols.length === 1 && result.rows.length > 0 ? 'hidden' : ''}`}>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                    <tr>{visibleCols.map((c) => <th key={c.id} className="text-left px-3 py-1.5 font-semibold whitespace-nowrap">{c.alias}</th>)}</tr>
                  </thead>
                  <tbody>
                    {result.rows.length === 0 && (
                      <tr><td colSpan={visibleCols.length} className="px-3 py-8 text-center text-gray-500">
                        {result.template.scope === 'HISTORICAL' && (result.cisco_platform_boundary || result.requires_control_hub_admin_auth)
                          ? 'No live data — use the buttons above to open Cisco Analyzer or upload an exported XLSX.'
                          : result.template.scope === 'REALTIME' && result.data_kind === 'live_snapshot'
                          ? 'No active tasks right now for this target.'
                          : 'No data — see note above.'}
                      </td></tr>
                    )}
                    {result.rows.slice(0, 500).map((r, i) => (
                      <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                        {visibleCols.map((c) => {
                          const raw = r[c.id] ?? r[c.name] ?? r[c.alias];
                          const type = (result.column_types ?? {})[c.id] as CellType | undefined;
                          return <td key={c.id} className="px-3 py-1.5 whitespace-nowrap">{formatCellForDisplay(raw, type)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CiscoBoundaryPanel — the honest no-data panel for HISTORICAL templates.
// BUILD-TASK-oauth-diagnose-and-honest-fallback Fix 5.
//
// Why this panel exists: WxCC Analyzer historical data is gated to the Control
// Hub browser session — not to any OAuth token (Service App OR admin
// Integration). The post-connect probe confirmed every candidate URL returned
// 404/530/500 even with a valid admin token. So "Connect → re-run" is wrong;
// the honest options are:
//   1. Open in Cisco Analyzer  — deep-link to this visualisation in the admin's
//                                already-authed Control Hub browser session.
//   2. Upload Analyzer Export  — admin downloads the XLSX from Analyzer once,
//                                uploads it here, team views it in TriNetHub.
// A tertiary link surfaces prior uploads for this template + target so the
// team doesn't re-upload the same report twice.
// ============================================================================
function CiscoBoundaryPanel({
  templateId, templateScope, analyzerLink, targetType, targetId, targetName,
}: {
  templateId: string;
  templateScope: string;
  analyzerLink?: string;
  targetType: 'team' | 'csq';
  targetId: string;
  targetName: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<{ row_count: number; rows: Array<Record<string, any>> } | null>(null);
  const [showPrior, setShowPrior] = useState(false);
  const [priorBusy, setPriorBusy] = useState(false);
  const [priorErr, setPriorErr] = useState<string | null>(null);
  const [prior, setPrior] = useState<WxccReportExport[] | null>(null);
  const [openedExport, setOpenedExport] = useState<{ export_id: string; row_count: number; rows: Array<Record<string, any>> } | null>(null);
  const [openedBusy, setOpenedBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const link = analyzerLink ?? 'https://analyzer-v2.wxcc-us1.cisco.com/analyzer/home';

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setUploadErr(null);
    try {
      const r = await api.wxccTemplateUploadExport(templateId, {
        file: f, target_type: targetType, target_id: targetId,
      });
      setUploaded({ row_count: r.row_count, rows: r.rows });
    } catch (err: any) {
      setUploadErr(err?.message ?? 'upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const togglePrior = async () => {
    const next = !showPrior;
    setShowPrior(next);
    if (next && !prior) {
      setPriorBusy(true); setPriorErr(null);
      try {
        const r = await api.wxccTemplateListExports(templateId, targetId, targetType);
        setPrior(r.items);
      } catch (err: any) {
        setPriorErr(err?.message ?? 'list failed');
      } finally { setPriorBusy(false); }
    }
  };

  const openExport = async (export_id: string) => {
    setOpenedBusy(true);
    try {
      const r = await api.wxccTemplateGetExport(templateId, export_id);
      setOpenedExport({ export_id: r.export_id, row_count: r.row_count, rows: r.rows });
    } catch (err: any) {
      setUploadErr(err?.message ?? 'fetch failed');
    } finally { setOpenedBusy(false); }
  };

  const previewRows = openedExport?.rows ?? uploaded?.rows ?? [];
  const previewCount = openedExport?.row_count ?? uploaded?.row_count ?? 0;
  const previewCols = previewRows.length > 0 ? Object.keys(previewRows[0]!) : [];

  return (
    <div className="px-4 py-3 space-y-3 text-xs bg-amber-50 dark:bg-amber-900/15 border-b border-amber-200 dark:border-amber-900/30">
      <div>
        <div className="font-semibold text-amber-800 dark:text-amber-200">Cisco platform boundary</div>
        <div className="text-amber-700 dark:text-amber-300 mt-1">
          WxCC Analyzer {templateScope.toLowerCase()} data is gated to the Control Hub browser session, not to any
          OAuth token. Two ways forward:
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <a href={link} target="_blank" rel="noopener noreferrer"
           className="px-3 py-1.5 rounded-lg bg-tnh-accent text-white text-xs font-semibold hover:opacity-90 inline-flex items-center gap-1">
          Open in Cisco Analyzer <span aria-hidden>↗</span>
        </a>
        <label className={`px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark text-xs font-semibold cursor-pointer hover:bg-amber-100/40 dark:hover:bg-amber-900/30 ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
          {uploading ? 'Uploading…' : 'Upload Analyzer Export'}
          <input ref={fileRef} type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                 className="hidden" onChange={onPickFile} disabled={uploading} />
        </label>
        <button onClick={togglePrior}
                className="text-[11px] text-tnh-accent hover:underline">
          {showPrior ? 'Hide' : 'View'} prior uploaded exports
        </button>
      </div>
      {uploadErr && <div className="text-rose-600 dark:text-rose-300">{uploadErr}</div>}
      {showPrior && (
        <div className="border border-amber-200 dark:border-amber-900/30 rounded-md p-2 bg-white/40 dark:bg-black/20">
          <div className="font-semibold mb-1 text-amber-800 dark:text-amber-200">
            Prior exports for {targetType === 'team' ? 'team' : 'queue'} {targetName}
          </div>
          {priorBusy && <div className="text-gray-500">Loading…</div>}
          {priorErr && <div className="text-rose-600 dark:text-rose-300">{priorErr}</div>}
          {!priorBusy && !priorErr && (prior?.length ?? 0) === 0 && (
            <div className="text-gray-500">No prior uploads yet.</div>
          )}
          {(prior ?? []).map((p) => (
            <div key={p.export_id} className="flex items-center justify-between gap-3 py-1 border-t border-amber-200/40 dark:border-amber-900/20 first:border-t-0">
              <div className="font-mono text-[11px]">
                <span className="font-semibold">{p.as_of_date}</span>
                <span className="text-gray-500"> · {p.row_count} rows · {p.source_filename || '(no filename)'}</span>
                <span className="text-gray-400"> · uploaded {new Date(p.uploaded_at).toLocaleString()} by {p.uploaded_by}</span>
              </div>
              <button onClick={() => openExport(p.export_id)} disabled={openedBusy}
                      className="text-tnh-accent hover:underline">Open</button>
            </div>
          ))}
        </div>
      )}
      {previewRows.length > 0 && (
        <div className="border border-amber-200 dark:border-amber-900/30 rounded-md overflow-hidden">
          <div className="px-2 py-1 bg-amber-100/60 dark:bg-amber-900/20 flex items-center justify-between">
            <span className="font-semibold text-amber-800 dark:text-amber-200">
              {openedExport ? 'Prior export' : 'Uploaded export'} — {previewCount} row(s)
            </span>
            <button onClick={() => downloadCsv(`analyzer-export-${targetId}.csv`, previewRows)}
                    className="text-tnh-accent hover:underline">↓ CSV</button>
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-[11px]">
              <thead className="bg-white/60 dark:bg-black/30 sticky top-0">
                <tr>{previewCols.map((c) => <th key={c} className="text-left px-2 py-1 font-semibold whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 500).map((r, i) => (
                  <tr key={i} className="border-t border-amber-200/40 dark:border-amber-900/20">
                    {previewCols.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap font-mono">{String(r[c] ?? '')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
